import { WatchQueryFetchPolicy } from '@apollo/client';

import { IBackendCart, IHistoricalOrder } from '@rbi-ctg/menu';
import {
  IGetUserOrdersQueryVariables,
  RbiOrderStatus,
  useGetUserOrdersQuery,
} from 'generated/rbi-graphql';
import { useAuthContext } from 'state/auth';
import { useAppSelector } from 'state/global-state';
import { selectLoyaltyUserTransactions } from 'state/global-state/models/loyalty/user/user.selectors';
import { ServiceMode } from 'state/service-mode/types';


export const UserOrdersDefaultVariables = {
  orderStatuses: [
    RbiOrderStatus.INSERT_SUCCESSFUL,
    RbiOrderStatus.UPDATE_SUCCESSFUL,
    RbiOrderStatus.CATERING_PLACED,
    RbiOrderStatus.CATERING_SUCCESSFUL,
    RbiOrderStatus.REFUND_SUCCESSFUL,
    RbiOrderStatus.REFUND_ERROR,
    RbiOrderStatus.REFUND_REQUESTED,
  ],
  shouldFilterByRegion: true,
};

export const useUserOrders = (
  variables: IGetUserOrdersQueryVariables,
  fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network',
  skip?: boolean
) => {
  const { isAuthenticated } = useAuthContext();
  const { data, error, loading, refetch } = useGetUserOrdersQuery({
    variables: {
      ...UserOrdersDefaultVariables,
      ...variables,
    },
    context: { shouldNotBatch: true },
    skip: !isAuthenticated || skip,
    fetchPolicy,
  });

  const count = data?.userOrders.count || 0;
  const orders = (data?.userOrders.orders || []) as unknown as IHistoricalOrder[];

  const loyaltyTransactions = useAppSelector(selectLoyaltyUserTransactions);
  const inStoreTransactions = loyaltyTransactions
    .filter(transaction => transaction.channel === 'RESTAURANT')
    .map((transaction): IHistoricalOrder => {
      return {
        createdAt: transaction.createdAt,
        cart: {
          cartEntries:
            transaction.transactionDetails?.orders // orders are items in the cart
              // filter out children orders and display only the parent order
              // children orders are modifiers for the parent order
              .filter(order => !order.parentReferenceId)
              .map(order => ({
                sanityId: order.productId,
                quantity: order.quantity,
                name: order.name,
                price: order.price,
              })) ?? [],
          serviceMode: ServiceMode.EAT_IN,
        } as unknown as IBackendCart,
        loyaltyTransaction: transaction,
      } as unknown as IHistoricalOrder;
    });

  return {
    error,
    loading,
    orders: [...orders, ...inStoreTransactions]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 30), // limit to 30 orders to avoid overwhelming the UI
    count: count + inStoreTransactions.length,
    refetch,
  };
};
